import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Facebook, Chrome, BarChart3, PieChart as PieChartIcon, Eye } from 'lucide-react';
import { AuthContext } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useDateRange } from '@/contexts/DateRangeContext';
import DateRangePicker, { getDateRange } from '@/components/DateRangePicker';
import DashboardLayout from '@/components/DashboardLayout';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const META_COLOR = '#1877F2';
const GOOGLE_COLOR = '#EA4335';

// Outside component — stable, never changes
const PRESET_MAP = {
  'Hoy': 'today', 'Ayer': 'yesterday',
  'Últimos 7 días': '7d', 'Últimos 15 días': '15d',
  'Últimos 30 días': '30d', 'Últimos 90 días': '90d',
  'Este mes': 'this_month', 'Mes anterior': 'last_month',
};

const fmt = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n/1000).toFixed(1)}K`;
  return Math.round(n).toLocaleString();
};

const fmtUsd = (n) => {
  if (!n) return '$0';
  if (n >= 1000000) return `$${(n/1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n/1000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

// Calculate score for live API data (when DB is not used)
function scoreCampaigns(campaigns) {
  const scored = campaigns.map(c => {
    const spent = parseFloat(c.spent) || 0;
    const impressions = parseInt(c.impressions) || 0;
    const clicks = parseInt(c.clicks) || 0;
    const conversions = parseFloat(c.conversions) || 0;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    const roi = spent > 0 ? (conversions / spent) * 100 : 0;
    const cpc = clicks > 0 ? spent / clicks : 0;

    const ctrScore = Math.min(ctr * 20, 100);
    const convRate = clicks > 0 ? (conversions / clicks * 100) : 0;
    const convScore = Math.min(convRate * 5, 100);
    const cprScore = conversions > 0 ? Math.max(0, Math.min(100, 50)) : 0;
    const score = Math.max(0, Math.min(100, Math.round(ctrScore * 0.3 + convScore * 0.5 + cprScore * 0.2)));
    const decision = score >= 75 ? 'scale' : score >= 50 ? 'optimize' : 'pause';

    return { ...c, spent, impressions, clicks, conversions, ctr: +ctr.toFixed(2), roi: +roi.toFixed(2), cpc: +cpc.toFixed(2), score, decision };
  });
  scored.sort((a, b) => b.score - a.score);
  
  const insights = [];
  scored.slice(0, 3).forEach(c => {
    if (c.decision === 'scale') insights.push({ type: 'scale', message: 'Escalar: ' + c.name + ' · CTR ' + c.ctr + '%' });
  });
  scored.forEach(c => {
    if (c.ctr < 1 && c.impressions > 1000) insights.push({ type: 'warning', message: 'CTR bajo: ' + c.name + ' (' + c.ctr + '%) · revisar creativos' });
    if (c.spent > 100 && c.conversions < 1) insights.push({ type: 'alert', message: 'Sin conversiones: ' + c.name + ' · gastó $' + Math.round(c.spent) });
  });

  return { campaigns: scored, ranking: scored.slice(0, 5), insights: insights.slice(0, 5) };
}

function Skeleton() {
  return (
    <DashboardLayout>
      <div className="p-8 animate-pulse">
        <div className="flex justify-between mb-8">
          <div className="h-8 w-64 bg-gray-200 rounded-lg" />
          <div className="h-10 w-44 bg-gray-200 rounded-xl" />
        </div>
        <div className="grid grid-cols-2 gap-6 mb-6">
          {[1,2].map(i => <div key={i} className="h-40 bg-gray-100 rounded-2xl" />)}
        </div>
        <div className="grid grid-cols-2 gap-6">
          {[1,2].map(i => <div key={i} className="h-64 bg-gray-100 rounded-2xl" />)}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default function Dashboard() {
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const { activeWorkspace, clientId } = useWorkspace();
  const { range: dateRange, setRange: setDateRange } = useDateRange();
  const [campaigns, setCampaigns] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [creatives, setCreatives] = useState({});
  const [insights, setInsights] = useState([]);
  const [ga4, setGa4] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Race condition protection: ignore stale responses
  const requestRef = useRef(0);

  useEffect(() => {
    if (!clientId) return;

    const requestId = ++requestRef.current;
    setLoading(true);

    const headers = { Authorization: `Bearer ${token}` };

    // Read from DB (fast), fallback to live API for today
    const dateFrom = dateRange.from || '';
    const dateTo = dateRange.to || '';
    const presetKey = PRESET_MAP[dateRange.label] || '30d';
    const isToday = dateRange.label === 'Hoy';

    const _d = new Date(); _d.setHours(_d.getHours() - 4); const todayStr = _d.toISOString().slice(0, 10);
    const metricsPromise = isToday
      ? Promise.all([
          axios.get(`${API}/meta/campaigns-live?range=${presetKey}&client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`, { headers })
            .then(r => r.data || {}).catch(() => ({})),
          axios.get(`${API}/dashboard/metrics?client_id=${clientId}&date_from=${todayStr}&date_to=${todayStr}`, { headers })
            .then(r => r.data || {}).catch(() => ({})),
        ]).then(([metaLive, dbMetrics]) => {
          const allCamps = (dbMetrics?.campaigns || []);
          const s = scoreCampaigns(allCamps);
          return { ...dbMetrics, ...s, campaigns: allCamps, source: 'live' };
        }).catch(() => ({}))
      : Promise.all([
          axios.get(`${API}/meta/campaigns-live?range=${presetKey}&client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`, { headers })
            .then(r => r.data || {}).catch(() => ({})),
          axios.get(`${API}/dashboard/metrics?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`, { headers })
            .then(r => r.data || {}).catch(() => ({})),
        ]).then(([metaLive, dbMetrics]) => {
          const allCamps = (dbMetrics?.campaigns || []);
          const s = scoreCampaigns(allCamps);
          return { ...dbMetrics, ...s, campaigns: allCamps, source: 'mixed' };
        }).catch(() => ({}));

    Promise.all([
      metricsPromise,
      axios.get(`${API}/ga4/metrics?client_id=${clientId}`, { headers }).catch(() => ({ data: null })),
      axios.get(`${API}/gsc/metrics?client_id=${clientId}`, { headers }).catch(() => ({ data: null })),
    ]).then(([campsRes, ga4Res, gscRes]) => {
      if (requestId !== requestRef.current) return;

      setCampaigns(campsRes?.campaigns || []);
      const ranked = campsRes?.ranking || [];
      setRanking(ranked);
      // Cargar thumbnails de Meta
      const token = localStorage.getItem('token');
      const map = {};
      Promise.all(ranked.filter(camp => camp.platform === 'meta').map(async (camp) => {
        try {
          const r = await fetch(`${API}/campaigns/${camp.id}/creatives`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.ok) {
            const data = await r.json();
            const list = Array.isArray(data) ? data : (data.creatives || []);
            const thumb = list[0]?.thumbnail_url || list[0]?.image_url;
            if (thumb) map[camp.id] = thumb;
          }
        } catch(e) {}
      })).then(() => setCreatives(map));
      setInsights(campsRes?.insights || []);
      if (ga4Res?.data?.connected) setGa4(ga4Res.data);
      else setGa4(null);
      if (gscRes?.data?.connected) setGsc(gscRes.data);
      else setGsc(null);
    }).catch(e => {
      if (requestId !== requestRef.current) return;
      console.error('Dashboard fetch error:', e);
    }).finally(() => {
      if (requestId !== requestRef.current) return;
      setLoading(false);
    });
  }, [clientId, dateRange.label, token]);

  // No client selected — show empty state
  if (!clientId) return (
    <DashboardLayout>
      <div className="p-8 flex flex-col items-center justify-center min-h-[70vh]">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200">
            <BarChart3 className="w-10 h-10 text-white" />
          </div>
          <h2 className={`text-3xl font-bold mb-3 ${theme.text}`}>Bienvenido a AdPulse AI</h2>
          <p className={`${theme.textSecondary} mb-8 text-lg leading-relaxed`}>Para comenzar, crea tu primer cliente y conecta tus cuentas de Meta Ads, Google Ads o GA4.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/clients" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-full font-semibold transition-all shadow-md shadow-blue-200 inline-flex items-center gap-2">
              Crear mi primer cliente →
            </a>
          </div>
          <div className={`mt-10 grid grid-cols-3 gap-4`}>
            <div className={`${theme.glassCard} rounded-xl p-4`}>
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-blue-50 flex items-center justify-center">
                <Facebook className="w-5 h-5 text-blue-600" />
              </div>
              <p className={`text-xs font-medium ${theme.textSecondary}`}>Meta Ads</p>
            </div>
            <div className={`${theme.glassCard} rounded-xl p-4`}>
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-red-50 flex items-center justify-center">
                <Chrome className="w-5 h-5 text-red-500" />
              </div>
              <p className={`text-xs font-medium ${theme.textSecondary}`}>Google Ads</p>
            </div>
            <div className={`${theme.glassCard} rounded-xl p-4`}>
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-yellow-50 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-yellow-600" />
              </div>
              <p className={`text-xs font-medium ${theme.textSecondary}`}>Analytics</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );

  if (loading) return <Skeleton />;

  const meta = campaigns.filter(c => c.platform === 'meta');
  const google = campaigns.filter(c => c.platform === 'google');
  const sum = (arr, k) => arr.reduce((s,c) => s + (parseFloat(c[k])||0), 0);
  const avg = (arr, k) => arr.length ? sum(arr,k)/arr.length : 0;

  const metaStats = { spent: sum(meta,'spent'), impressions: sum(meta,'impressions'), clicks: sum(meta,'clicks'), conversions: sum(meta,'conversions'), ctr: avg(meta,'ctr'), roi: avg(meta,'roi') };
  const googleStats = { spent: sum(google,'spent'), impressions: sum(google,'impressions'), clicks: sum(google,'clicks'), conversions: sum(google,'conversions'), ctr: avg(google,'ctr'), roi: avg(google,'roi') };

  const filtered = activeTab === 'all' ? campaigns : campaigns.filter(c => c.platform === activeTab);
  const pieData = [
    { name: 'Meta Ads', value: metaStats.spent, color: META_COLOR },
    { name: 'Google Ads', value: googleStats.spent, color: GOOGLE_COLOR },
  ].filter(d => d.value > 0);
  const barData = [
    { metric: 'Gasto', Meta: metaStats.spent, Google: googleStats.spent },
    { metric: 'Clicks', Meta: metaStats.clicks, Google: googleStats.clicks },
    { metric: 'Conv.', Meta: metaStats.conversions, Google: googleStats.conversions },
  ];

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={`font-heading font-bold text-4xl ${theme.text}`}>Dashboard de Marketing</h1>
            <p className={`${theme.textSecondary} mt-1`}>
              {activeWorkspace ? `Cliente: ${activeWorkspace.name}` : 'Todas las cuentas'} · {dateRange.label}
            </p>
          </div>
          {/* DateRangePicker movido al layout global */}
        </div>

        {/* Platform tabs */}
        <div className="flex gap-2 mb-8">
          {[['all','Todas las Plataformas'],['meta','Meta Ads'],['google','Google Ads']].map(([val, label]) => (
            <button key={val} onClick={() => setActiveTab(val)}
              className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                activeTab === val
                  ? val === 'meta' ? 'bg-blue-600 text-white' : val === 'google' ? 'bg-red-500 text-white' : (theme.isDark ? 'bg-white text-black' : 'bg-gray-900 text-white')
                  : `${theme.glassCard} ${theme.text}`
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Platform cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Meta */}
          <motion.div initial={{opacity:0,x:-20}} animate={{opacity:1,x:0}}
            className={`${theme.glassCard} p-6 rounded-2xl border-l-4 border-blue-500 ${activeTab==='google'?'opacity-40':''}`}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Facebook className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${theme.text}`}>Meta Ads</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>{meta.length} campañas</p>
                </div>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">Facebook & Instagram</span>
            </div>
            <div className="grid grid-cols-4 gap-3 mb-4">
              {[['Gasto',fmtUsd(metaStats.spent),'text-blue-600'],['Impresiones',fmt(metaStats.impressions),theme.text],['Clicks',fmt(metaStats.clicks),theme.text],['Conv.',fmt(metaStats.conversions),'text-green-600']].map(([l,v,c]) => (
                <div key={l} className={`${theme.bgCard} rounded-xl p-3`}>
                  <p className={`text-xs ${theme.textSecondary} mb-1`}>{l}</p>
                  <p className={`font-bold text-lg ${c}`}>{v}</p>
                </div>
              ))}
            </div>
            <div className={`text-sm ${theme.textSecondary} flex gap-4`}>
              <span>CTR: <strong className={theme.text}>{metaStats.ctr.toFixed(2)}%</strong></span>
              <span>ROI: <strong className={metaStats.roi>=0?'text-green-600':'text-red-500'}>{metaStats.roi.toFixed(1)}%</strong></span>
            </div>
          </motion.div>

          {/* Google */}
          <motion.div initial={{opacity:0,x:20}} animate={{opacity:1,x:0}}
            className={`${theme.glassCard} p-6 rounded-2xl border-l-4 border-red-500 ${activeTab==='meta'?'opacity-40':''}`}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <Chrome className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${theme.text}`}>Google Ads</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>{google.length} campañas</p>
                </div>
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold">Search & Display</span>
            </div>
            <div className="grid grid-cols-4 gap-3 mb-4">
              {[['Gasto',fmtUsd(googleStats.spent),'text-red-500'],['Impresiones',fmt(googleStats.impressions),theme.text],['Clicks',fmt(googleStats.clicks),theme.text],['Conv.',fmt(googleStats.conversions),'text-green-600']].map(([l,v,c]) => (
                <div key={l} className={`${theme.bgCard} rounded-xl p-3`}>
                  <p className={`text-xs ${theme.textSecondary} mb-1`}>{l}</p>
                  <p className={`font-bold text-lg ${c}`}>{v}</p>
                </div>
              ))}
            </div>
            <div className={`text-sm ${theme.textSecondary} flex gap-4`}>
              <span>CTR: <strong className={theme.text}>{googleStats.ctr.toFixed(2)}%</strong></span>
              <span>ROI: <strong className={googleStats.roi>=0?'text-green-600':'text-red-500'}>{googleStats.roi.toFixed(1)}%</strong></span>
            </div>
          </motion.div>
        </div>

        {/* GA4 + GSC */}
        {(ga4 || gsc) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {ga4 && (
              <div className={`${theme.glassCard} p-6 rounded-2xl border-l-4 border-yellow-400`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className={`font-bold ${theme.text}`}>Google Analytics 4</h3>
                    <p className={`text-xs ${theme.textSecondary}`}>{ga4.property_name}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[['Sesiones',fmt(ga4.sessions)],['Usuarios',fmt(ga4.users)],['Pageviews',fmt(ga4.pageviews)],['Rebote',ga4.bounce_rate?.toFixed(1)+'%'],['Conv.',fmt(ga4.conversions)],['Dur.',Math.round(ga4.avg_session)+'s']].map(([l,v]) => (
                    <div key={l} className={`${theme.isDark?'bg-yellow-900/20':'bg-yellow-50'} p-2 rounded-lg`}>
                      <p className={`text-xs ${theme.textSecondary}`}>{l}</p>
                      <p className={`font-bold ${theme.text}`}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {gsc && (
              <div className={`${theme.glassCard} p-6 rounded-2xl border-l-4 border-green-400`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h3 className={`font-bold ${theme.text}`}>Search Console</h3>
                    <p className={`text-xs ${theme.textSecondary}`}>{gsc.site_url}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[['Clicks',fmt(gsc.clicks)],['Impresiones',fmt(gsc.impressions)],['CTR',gsc.ctr?.toFixed(2)+'%'],['Posición',gsc.position?.toFixed(1)]].map(([l,v]) => (
                    <div key={l} className={`${theme.isDark?'bg-green-900/20':'bg-green-50'} p-2 rounded-lg`}>
                      <p className={`text-xs ${theme.textSecondary}`}>{l}</p>
                      <p className={`font-bold ${theme.text}`}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className={`${theme.glassCard} p-6 rounded-2xl`}>
            <h3 className={`font-semibold ${theme.text} mb-4 flex items-center gap-2`}>
              <PieChartIcon className="w-4 h-4" /> Distribución de Gasto
            </h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value"
                    label={({name,value}) => `${name}: ${fmtUsd(value)}`} labelLine={false}>
                    {pieData.map((e,i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip formatter={fmtUsd} contentStyle={{background:theme.isDark?'#111':'#fff',border:'none',borderRadius:8}} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className={theme.textSecondary}>Sin datos de gasto</p>
              </div>
            )}
          </div>
          <div className={`${theme.glassCard} p-6 rounded-2xl`}>
            <h3 className={`font-semibold ${theme.text} mb-4 flex items-center gap-2`}>
              <BarChart3 className="w-4 h-4" /> Comparación de Plataformas
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={theme.isDark?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.08)'} />
                <XAxis type="number" stroke="#888" tickFormatter={fmt} fontSize={11} />
                <YAxis type="category" dataKey="metric" stroke="#888" width={50} fontSize={11} />
                <Tooltip formatter={fmt} contentStyle={{background:theme.isDark?'#111':'#fff',border:'none',borderRadius:8}} />
                <Legend />
                <Bar dataKey="Meta" fill={META_COLOR} name="Meta Ads" radius={[0,4,4,0]} />
                <Bar dataKey="Google" fill={GOOGLE_COLOR} name="Google Ads" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ranking */}
        {ranking.length > 0 && (
          <div className={`${theme.glassCard} p-6 rounded-2xl mb-8`}>
            <h3 className={`font-semibold text-lg ${theme.text} mb-4 flex items-center gap-2`}>
              🏆 Ranking de Campañas
            </h3>
            <div className="space-y-3">
              {ranking.map((c, i) => {
                const rankColor = i === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white' : i === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' : i === 2 ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white' : 'bg-gray-100 text-gray-500';
                const cpr = c.conversions > 0 ? c.spent / c.conversions : 0;
                const cpm = c.impressions > 0 ? (c.spent / c.impressions) * 1000 : 0;
                const statusColor = c.decision === 'scale' ? 'bg-green-500' : c.decision === 'optimize' ? 'bg-amber-400' : 'bg-red-400';
                const statusLabel = c.decision === 'scale' ? 'Escalar' : c.decision === 'optimize' ? 'Optimizar' : 'Revisar';
                return (
                  <div key={c.id || i} className={`p-4 rounded-xl border ${theme.border} ${theme.bgCard} hover:shadow-md transition-shadow`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shadow-sm ${rankColor}`}>{i + 1}</div>
                        {creatives[c.id] && (
                          <img src={creatives[c.id]} alt="" className="w-10 h-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700 flex-shrink-0" onError={e => e.target.style.display='none'} />
                        )}
                        <div>
                          <p className={`font-semibold ${theme.text}`}>{c.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className={`w-2 h-2 rounded-full ${statusColor}`}></div>
                            <span className={`text-xs ${theme.textSecondary}`}>{statusLabel} · Score {c.score}</span>
                          </div>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-blue-600">{fmtUsd(c.spent)}</span>
                    </div>
                    <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">Resultados</p><p className={`text-sm font-bold ${c.conversions > 0 ? 'text-green-600' : 'text-gray-400'}`}>{fmt(c.conversions || 0)}</p></div>
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">C/Resultado</p><p className={`text-sm font-bold ${theme.text}`}>{cpr > 0 ? fmtUsd(cpr) : '—'}</p></div>
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">Impresiones</p><p className={`text-sm font-bold ${theme.text}`}>{fmt(c.impressions)}</p></div>
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">Clicks</p><p className={`text-sm font-bold ${theme.text}`}>{fmt(c.clicks)}</p></div>
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">Alcance</p><p className={`text-sm font-bold ${theme.text}`}>{fmt(c.reach || 0)}</p></div>
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">CTR</p><p className="text-sm font-bold text-indigo-500">{c.ctr}%</p></div>
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">CPC</p><p className={`text-sm font-bold ${theme.text}`}>{fmtUsd(c.cpc || 0)}</p></div>
                      <div className={`${theme.isDark?'bg-white/5':'bg-gray-50'} rounded-lg p-2`}><p className="text-[10px] text-gray-400 uppercase">CPM</p><p className={`text-sm font-bold ${theme.text}`}>{fmtUsd(cpm)}</p></div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Insights */}
            {insights.length > 0 && (
              <div className="mt-4 space-y-2">
                {insights.map((ins, i) => (
                  <div key={i} className={`p-3 rounded-lg text-sm ${ins.type === 'scale' ? 'bg-green-50 text-green-700 border border-green-200' : ins.type === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {ins.type === 'scale' ? '🚀' : ins.type === 'warning' ? '⚠️' : '🔴'} {ins.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Campaigns table */}
        <div className={`${theme.glassCard} p-6 rounded-2xl`}>
          <div className="flex items-center justify-between mb-5">
            <h3 className={`font-semibold text-lg ${theme.text}`}>
              {activeTab==='all'?'Todas las Campañas':activeTab==='meta'?'Campañas Meta':'Campañas Google'}
            </h3>
            <span className={`text-sm ${theme.textSecondary}`}>{filtered.length} campañas</span>
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <p className={theme.textSecondary}>No hay campañas para mostrar en este período</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${theme.border}`}>
                    {['Campaña','Plataforma','Gasto','Impresiones','Clicks','CTR','Conv.','ROI'].map(h => (
                      <th key={h} className={`py-3 px-4 text-left font-mono text-xs uppercase tracking-wider ${theme.textSecondary}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c,i) => (
                    <tr key={c.id||i} className={`border-b ${theme.border} hover:bg-white/5 transition-colors`}>
                      <td className={`py-3 px-4 font-medium ${theme.text} max-w-xs truncate`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${c.platform==='meta'?'bg-blue-500':'bg-red-500'}`} />
                          {c.name}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${c.platform==='meta'?'bg-blue-100 text-blue-700':'bg-red-100 text-red-700'}`}>
                          {c.platform==='meta'?'Meta':'Google'}
                        </span>
                      </td>
                      <td className={`py-3 px-4 font-semibold text-blue-600`}>{fmtUsd(c.spent)}</td>
                      <td className={`py-3 px-4 ${theme.textSecondary}`}>{fmt(c.impressions)}</td>
                      <td className={`py-3 px-4 ${theme.textSecondary}`}>{fmt(c.clicks)}</td>
                      <td className={`py-3 px-4 text-indigo-500`}>{(parseFloat(c.ctr)||0).toFixed(2)}%</td>
                      <td className={`py-3 px-4 text-green-600 font-semibold`}>{fmt(c.conversions||0)}</td>
                      <td className={`py-3 px-4 font-bold ${(c.roi||0)>=0?'text-green-600':'text-red-500'}`}>
                        {(c.roi||0)>=0?'+':''}{(parseFloat(c.roi)||0).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
